import React, { useState, useEffect, useRef } from 'react';
import { getVisaServices, createVisaService, updateVisaStatus, deleteVisaService, getCustomers } from '../lib/api';
import api from '../lib/api';
import { VISA } from '../constants/testIds';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ExportImportButtons } from '../components/ExportImportButtons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { 
  Plus, 
  MoreVertical, 
  Trash2, 
  Eye, 
  CheckCircle2, 
  Clock, 
  FileText, 
  Loader2,
  Upload,
  Download,
  CreditCard,
  FileImage,
  X,
  Paperclip
} from 'lucide-react';
import { toast } from 'sonner';

const statusOptions = [
  { value: 'documents_pending', label: 'Documents Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'processing', label: 'Processing' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const visaTypes = [
  { value: 'tourist', label: 'Tourist' },
  { value: 'business', label: 'Business' },
  { value: 'transit', label: 'Transit' },
  { value: 'student', label: 'Student' },
  { value: 'work', label: 'Work' },
];

const documentTypes = [
  { value: 'aadhaar', label: 'Aadhaar Card', icon: CreditCard },
  { value: 'passport', label: 'Passport', icon: FileText },
  { value: 'visa', label: 'Visa Copy', icon: FileImage },
  { value: 'photo', label: 'Passport Photo', icon: FileImage },
  { value: 'other', label: 'Other Document', icon: Paperclip },
];

const getStatusClass = (status) => {
  const classes = {
    documents_pending: 'bg-amber-50 text-amber-700',
    submitted: 'bg-blue-50 text-blue-700',
    processing: 'bg-purple-50 text-purple-700',
    approved: 'bg-emerald-50 text-emerald-700',
    rejected: 'bg-red-50 text-red-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-700';
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'approved':
      return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    case 'rejected':
      return <FileText className="w-4 h-4 text-red-600" />;
    default:
      return <Clock className="w-4 h-4 text-amber-600" />;
  }
};

export const VisaServices = () => {
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedDocType, setSelectedDocType] = useState('passport');
  
  const [formData, setFormData] = useState({
    customer_id: '',
    country: '',
    visa_type: 'tourist',
    passport_number: '',
    aadhaar_number: '',
    application_date: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, customersRes] = await Promise.all([
        getVisaServices(),
        getCustomers(),
      ]);
      setServices(servicesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      country: '',
      visa_type: 'tourist',
      passport_number: '',
      aadhaar_number: '',
      application_date: '',
      notes: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await createVisaService(formData);
      toast.success('Visa application created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;

    try {
      await deleteVisaService(id);
      toast.success('Application deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await updateVisaStatus(id, status);
      toast.success('Status updated successfully');
      loadData();
      if (selectedService?.id === id) {
        setSelectedService({ ...selectedService, status });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewDetails = (service) => {
    setSelectedService(service);
    setSheetOpen(true);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  // Document Upload Handler
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedService) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', selectedDocType);
      formData.append('visa_service_id', selectedService.id);

      await api.post('/visa-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully!');
      
      // Refresh service details
      const { data } = await api.get(`/visa/${selectedService.id}`);
      setSelectedService(data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/visa-documents/${docId}`);
      toast.success('Document deleted');
      // Refresh
      const { data } = await api.get(`/visa/${selectedService.id}`);
      setSelectedService(data);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="visa-container" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>
            Visa & Passport Services
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage visa applications and store identity documents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportImportButtons module="visa" onImportSuccess={loadData} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid={VISA.addButton}
                onClick={() => { resetForm(); setDialogOpen(true); }} 
                className="text-white" 
                style={{ backgroundColor: '#383838' }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-outfit" style={{ color: '#84bd0e' }}>New Visa Application</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country *</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="e.g., UAE"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Visa Type</Label>
                    <Select value={formData.visa_type} onValueChange={(v) => setFormData({ ...formData, visa_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {visaTypes.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Passport Number</Label>
                    <Input
                      value={formData.passport_number}
                      onChange={(e) => setFormData({ ...formData, passport_number: e.target.value.toUpperCase() })}
                      placeholder="e.g., A1234567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aadhaar Number</Label>
                    <Input
                      value={formData.aadhaar_number}
                      onChange={(e) => setFormData({ ...formData, aadhaar_number: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                      placeholder="e.g., 123456789012"
                      maxLength={12}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Application Date</Label>
                  <Input
                    type="date"
                    value={formData.application_date}
                    onChange={(e) => setFormData({ ...formData, application_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional details..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="text-white" style={{ backgroundColor: '#383838' }} disabled={submitting}>
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Application
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Customer</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Visa Type</TableHead>
                <TableHead>Passport No.</TableHead>
                <TableHead>Aadhaar No.</TableHead>
                <TableHead>Documents</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.length > 0 ? (
                services.map((s) => (
                  <TableRow key={s.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{getCustomerName(s.customer_id)}</TableCell>
                    <TableCell>{s.country}</TableCell>
                    <TableCell className="capitalize">{s.visa_type}</TableCell>
                    <TableCell className="font-mono text-sm">{s.passport_number || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {s.aadhaar_number ? `${s.aadhaar_number.slice(0,4)} **** ${s.aadhaar_number.slice(-4)}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        <Paperclip className="w-3 h-3 mr-1" />
                        {s.documents?.length || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusClass(s.status)}>
                        {getStatusIcon(s.status)}
                        <span className="ml-1 capitalize">{s.status?.replace('_', ' ')}</span>
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetails(s)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View & Upload Docs
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {statusOptions.map((opt) => (
                            <DropdownMenuItem key={opt.value} onClick={() => handleStatusUpdate(s.id, opt.value)}>
                              {opt.label}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(s.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No visa applications yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Details Sheet with Document Upload */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-outfit" style={{ color: '#84bd0e' }}>
              Application Details
            </SheetTitle>
          </SheetHeader>
          
          {selectedService && (
            <div className="mt-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Customer</p>
                  <p className="font-medium">{getCustomerName(selectedService.customer_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Country</p>
                  <p className="font-medium">{selectedService.country}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Visa Type</p>
                  <p className="font-medium capitalize">{selectedService.visa_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={getStatusClass(selectedService.status)}>
                    {selectedService.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Passport Number</p>
                  <p className="font-mono">{selectedService.passport_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aadhaar Number</p>
                  <p className="font-mono">{selectedService.aadhaar_number || '-'}</p>
                </div>
              </div>

              {/* Document Upload Section */}
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#84bd0e' }}>
                  <Paperclip className="w-5 h-5" />
                  Identity Documents
                </h3>

                {/* Upload Form */}
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-sm">Document Type</Label>
                      <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {documentTypes.map((dt) => (
                            <SelectItem key={dt.value} value={dt.value}>
                              {dt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={handleDocumentUpload}
                      />
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingDoc}
                        className="w-full text-white"
                        style={{ backgroundColor: '#383838' }}
                      >
                        {uploadingDoc ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload Document
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Supported formats: PDF, JPG, PNG, WebP (Max 10MB)
                  </p>
                </div>

                {/* Uploaded Documents List */}
                <div className="space-y-2">
                  {selectedService.documents?.length > 0 ? (
                    selectedService.documents.map((doc) => {
                      const docType = documentTypes.find(d => d.value === doc.document_type);
                      const DocIcon = docType?.icon || Paperclip;
                      return (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-[#383838] transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#84bd0e]/10 flex items-center justify-center">
                              <DocIcon className="w-5 h-5 text-[#84bd0e]" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{docType?.label || doc.document_type}</p>
                              <p className="text-xs text-muted-foreground">
                                Uploaded {formatDate(doc.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.file_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(doc.file_url, '_blank')}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Paperclip className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>No documents uploaded yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedService.notes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="mt-1">{selectedService.notes}</p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
